import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import BottomNav from '../components/BottomNav';
import LessonBannerAd from '../components/LessonBannerAd';
import { fetchWordCategories } from '../api/wordsApi';
import type { WordCategoryRow } from '../api/wordsApi';
import '../screens/WordsScreen.css';

const HARDCODED_SLUGS = [
  'abc', 'numbers', 'fruits', 'animals', 'colors', 'body', 'food', 'family', 'clothes', 'vehicles',
  'weather', 'greetings', 'commonWords', 'advancedWords', 'actions', 'time', 'places', 'school',
  'emotions', 'sports', 'suffix_azione', 'suffix_zione', 'suffix_mente', 'suffix_ita', 'suffix_ore_atore',
  'suffix_oso', 'suffix_issimo', 'verbs_infinitive', 'prefix_ri',
] as const;

const DEFAULT_ICON: Record<string, string> = {
  abc: '🔤', numbers: '🔢', fruits: '🍎', animals: '🐶', colors: '🎨', body: '👤', food: '🍽️',
  family: '👨‍👩‍👧‍👦', clothes: '👕', vehicles: '🚗', weather: '☀️', greetings: '👋',
  commonWords: '📝', advancedWords: '⭐', actions: '🏃', time: '⏰', places: '🏛️', school: '🎓',
  emotions: '😊', sports: '⚽', suffix_azione: '📚', suffix_zione: '📝', suffix_mente: '⚡',
  suffix_ita: '🏛️', suffix_ore_atore: '💼', suffix_oso: '✨', suffix_issimo: '⭐',
  verbs_infinitive: '📖', prefix_ri: '🔄',
};

// Map DB icon names (e.g. "tshirt.fill", "apple") to emoji for display
const ICON_NAME_TO_EMOJI: Record<string, string> = {
  'textformat.abc': '🔤', number: '🔢', numbers: '🔢', apple: '🍎', fruits: '🍎',
  'pawprint.fill': '🐶', animals: '🐶', 'paintpalette.fill': '🎨', colors: '🎨',
  'figure.stand': '👤', body: '👤', 'fork.knife': '🍽️', food: '🍽️',
  'person.2.fill': '👨‍👩‍👧‍👦', family: '👨‍👩‍👧‍👦', 'tshirt.fill': '👕', clothes: '👕',
  'car.fill': '🚗', vehicles: '🚗', 'cloud.sun.fill': '☀️', weather: '☀️',
  'hand.wave.fill': '👋', greetings: '👋', 'doc.text.fill': '📝', commonWords: '📝',
  'star.fill': '⭐', advancedWords: '⭐', 'bolt.fill': '🏃', actions: '🏃',
  'clock.fill': '⏰', time: '⏰', 'mappin.circle.fill': '🏛️', places: '🏛️',
  'book.fill': '🎓', school: '🎓', 'face.smiling.fill': '😊', emotions: '😊',
  'sportscourt.fill': '⚽', sports: '⚽',
};

function iconForDisplay(slug: string, iconFromApi?: string | null): string {
  if (iconFromApi && iconFromApi.trim()) {
    const key = iconFromApi.trim().toLowerCase();
    if (ICON_NAME_TO_EMOJI[key]) return ICON_NAME_TO_EMOJI[key];
    if (iconFromApi.length <= 3 && !/^[a-z.]/i.test(iconFromApi)) return iconFromApi;
  }
  return DEFAULT_ICON[slug] || '📦';
}

// Map URL slug to content key (e.g. suffix_azione -> suffixAzione)
function slugToContentKey(slug: string): string {
  const map: Record<string, string> = {
    suffix_azione: 'suffixAzione', suffix_zione: 'suffixZione', suffix_mente: 'suffixMente',
    suffix_ita: 'suffixIta', suffix_ore_atore: 'suffixOreAtore', suffix_oso: 'suffixOso',
    suffix_issimo: 'suffixIssimo', verbs_infinitive: 'verbsInfinitive', prefix_ri: 'prefixRi',
  };
  return map[slug] || slug;
}

const WordsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const [categoriesFromApi, setCategoriesFromApi] = useState<WordCategoryRow[] | null>(null);

  useEffect(() => {
    fetchWordCategories().then((data) => {
      if (data && data.length > 0) setCategoriesFromApi(data);
      else setCategoriesFromApi(null);
    });
  }, []);

  // Language content
  const content = {
    bn: {
      title: "শব্দ শিখুন",
      abc: "বর্ণমালা",
      numbers: "সংখ্যা",
      fruits: "ফল",
      animals: "প্রাণী",
      colors: "রঙ",
      body: "শরীরের অংশ",
      food: "খাবার",
      family: "পরিবার",
      clothes: "পোশাক",
      vehicles: "যানবাহন",
      weather: "আবহাওয়া",
      greetings: "অভিবাদন",
      commonWords: "শব্দ",
      advancedWords: "আয়ত্ত",
      actions: "ক্রিয়া",
      time: "সময়",
      places: "স্থান",
      school: "স্কুল",
      emotions: "আবেগ",
      sports: "খেলা",
      suffixAzione: "শব্দ",
      suffixZione: "শব্দ",
      suffixMente: "ক্রিয়াবিশেষণ",
      suffixIta: "শব্দ",
      suffixOreAtore: "পেশা",
      suffixOso: "বিশেষণ",
      suffixIssimo: "তীব্রতা",
      verbsInfinitive: "ক্রিয়া",
      prefixRi: "উপসর্গ"
    },
    it: {
      title: "Impara Parole",
      abc: "Alfabeto",
      numbers: "Numeri",
      fruits: "Frutta",
      animals: "Animali",
      colors: "Colori",
      body: "Parti del Corpo",
      food: "Cibo",
      family: "Famiglia",
      clothes: "Vestiti",
      vehicles: "Veicoli",
      weather: "Tempo",
      greetings: "Saluti",
      commonWords: "Parole\nComuni",
      advancedWords: "Parole\nAvanzate",
      actions: "Azioni",
      time: "Tempo",
      places: "Luoghi",
      school: "Scuola",
      emotions: "Emozioni",
      sports: "Sport",
      suffixAzione: "Azione",
      suffixZione: "Zione",
      suffixMente: "Mente",
      suffixIta: "Ità",
      suffixOreAtore: "Lavori",
      suffixOso: "Oso",
      suffixIssimo: "Issimo",
      verbsInfinitive: "Verbi",
      prefixRi: "Ri"
    },
    en: {
      title: "Learn Words",
      abc: "Alphabet",
      numbers: "Numbers",
      fruits: "Fruits",
      animals: "Animals",
      colors: "Colors",
      body: "Body Parts",
      food: "Food",
      family: "Family",
      clothes: "Clothes",
      vehicles: "Vehicles",
      weather: "Weather",
      greetings: "Greetings",
      commonWords: "Common\nWords",
      advancedWords: "Advanced\nWords",
      actions: "Actions",
      time: "Time",
      places: "Places",
      school: "School",
      emotions: "Emotions",
      sports: "Sports",
      suffixAzione: "Words",
      suffixZione: "Words",
      suffixMente: "Adverbs",
      suffixIta: "Words",
      suffixOreAtore: "Jobs",
      suffixOso: "Adjectives",
      suffixIssimo: "Intensifiers",
      verbsInfinitive: "Verbs",
      prefixRi: "Prefix"
    }
  };

  const handleCardClick = (type: string) => {
    navigate(`/words/${type}`);
  };

  const DEFAULT_COLORS = ['#8b5cf6', '#22c55e', '#f59e0b', '#3b82f6', '#ec4899', '#f59e0b', '#22c55e', '#8b5cf6', '#3b82f6', '#f59e0b', '#3b82f6', '#22c55e', '#8b5cf6', '#f59e0b', '#22c55e', '#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899', '#22c55e'];

  // Use categories from API if available; otherwise fallback to hardcoded list with content labels
  const categoriesToShow: { slug: string; title_it: string; title_bn: string; title_en: string; icon: string; color: string }[] =
    categoriesFromApi && categoriesFromApi.length > 0
      ? categoriesFromApi.map((c, i) => ({
          slug: c.slug,
          title_it: c.title_it,
          title_bn: c.title_bn,
          title_en: c.title_en,
          icon: iconForDisplay(c.slug, c.icon),
          color: (c.color && c.color.trim()) ? c.color : DEFAULT_COLORS[i % DEFAULT_COLORS.length],
        }))
      : HARDCODED_SLUGS.map((slug, i) => {
          const key = slugToContentKey(slug) as keyof typeof content.it;
          return {
            slug,
            title_it: content.it[key] || '',
            title_bn: content.bn[key] || '',
            title_en: content.en[key] || '',
            icon: DEFAULT_ICON[slug] || '📦',
            color: DEFAULT_COLORS[i % DEFAULT_COLORS.length],
          };
        });

  return (
    <div className="words-screen">
      {/* Header */}
      <div className="screen-header">
        <button 
          className="header-btn back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="header-center">
          <h1>{content[currentLanguage].title}</h1>
        </div>
        <button 
          className="header-btn settings-btn"
          onClick={() => navigate('/settings')}
          aria-label="Open settings"
        >
          ⋯
        </button>
      </div>

      <LessonBannerAd />

      {/* Featured Cards */}
      <div className="featured-section">
        <div className="featured-cards">
          {categoriesToShow.map((cat) => (
            <div
              key={cat.slug}
              className="featured-card practice"
              onClick={() => handleCardClick(cat.slug)}
              style={{
              backgroundColor: cat.color ? `${cat.color}22` : undefined,
              borderLeft: cat.color ? `4px solid ${cat.color}` : undefined,
            }}
            >
              <div className="card-header">
                <span className="card-title">
                  <span className="card-title-line">{cat.title_it}</span>
                  <span className="card-title-line">{cat.title_bn}</span>
                </span>
                <span className="heart-icon">♡</span>
              </div>
              <div className="card-icon">{cat.icon}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default WordsScreen;

